import { PointerEvent } from 'react';
import { m, useMotionValue, useSpring } from 'framer-motion';
// @mui
import { Box } from '@mui/material';
//
import { Product } from '../../data/products';
import { CONTAINER } from '../../theme/stage';
import { ANCHOR, CLOSING, productLinkAriaLabel } from './copy';
import ActionLink from './primitives/ActionLink';
import BisqueRing from './primitives/BisqueRing';
import Eyebrow from './primitives/Eyebrow';
import Reveal from './primitives/Reveal';
import TwoTone from './primitives/TwoTone';
import useStageMode from './useStageMode';

// ----------------------------------------------------------------------

type Props = {
  products: Product[];
};

const MAX_TURN = 12;

const TURN_SPRING = { stiffness: 60, damping: 18 };

export default function Closing({ products }: Props) {
  const { finePointer, reduced } = useStageMode();

  const pointer = finePointer && !reduced;

  const target = useMotionValue(0);

  const turn = useSpring(target, TURN_SPRING);

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    if (event.pointerType === 'touch') {
      return;
    }
    const half = document.documentElement.clientWidth / 2;
    target.set(MAX_TURN * Math.max(-1, Math.min(1, (event.clientX - half) / half)));
  };

  return (
    <Box
      component="section"
      id={ANCHOR.closing}
      aria-labelledby={CLOSING.titleId}
      onPointerMove={pointer ? onPointerMove : undefined}
      onPointerLeave={pointer ? () => target.set(0) : undefined}
      sx={{
        pt: { xs: '88px', sm: '160px', lg: '200px' },
        pb: { xs: '120px', sm: '160px', lg: '200px' },
        scrollMarginTop: { xs: '-88px', sm: '-160px', lg: '-200px' },
        '&:focus': { outline: 'none' },
        textAlign: 'center',
      }}
    >
      <Box sx={{ ...CONTAINER, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <m.span
          style={{ display: 'block', willChange: pointer ? 'transform' : undefined, rotate: turn }}
        >
          <BisqueRing size="closing" enter="view" />
        </m.span>

        <TwoTone
          as="h2"
          id={CLOSING.titleId}
          lines={CLOSING.lines}
          size="billboard"
          lineMode="block"
          align="center"
          enter="view"
          sx={{ mt: { xs: '40px', sm: '48px', lg: '56px' } }}
        />

        {products.length > 1 && (
          <Reveal delay={160}>
            <Eyebrow
              align="center"
              sx={{ m: { xs: '40px 0 16px', sm: '48px 0 16px', lg: '56px 0 16px' } }}
            >
              {CLOSING.productsLabel}
            </Eyebrow>
            <Box
              component="ul"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '12px',
                listStyle: 'none',
                m: 0,
                p: 0,
              }}
            >
              {products.map((product) => (
                <li key={product.id}>
                  <ActionLink
                    variant="product"
                    href={product.url}
                    ariaLabel={productLinkAriaLabel(product.name, new URL(product.url).hostname)}
                    logo={product.logo}
                  >
                    {product.name}
                  </ActionLink>
                </li>
              ))}
            </Box>
          </Reveal>
        )}
      </Box>
    </Box>
  );
}
